/**
 * Typed(-ish) endpoint functions.
 *
 * These are the ONLY places outside `client.js` that construct URLs; page
 * components must import from here and never touch `apiFetch` directly.
 */
import { apiFetch } from "./client";

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const authApi = {
  login: (email, password) =>
    apiFetch("/auth/login", { method: "POST", body: { email, password } }),
  logout: () => apiFetch("/auth/logout", { method: "POST" }),
  me: () => apiFetch("/auth/me"),
};

// ---------------------------------------------------------------------------
// Workspaces
// ---------------------------------------------------------------------------

export const workspacesApi = {
  list: () => apiFetch("/workspaces"),
  get: (id) => apiFetch(`/workspaces/${id}`),
  create: (body) => apiFetch("/workspaces", { method: "POST", body }),
  listMembers: (wsId) => apiFetch(`/workspaces/${wsId}/members`),
  addMember: (wsId, body) =>
    apiFetch(`/workspaces/${wsId}/members`, { method: "POST", body }),
  removeMember: (wsId, userId) =>
    apiFetch(`/workspaces/${wsId}/members/${userId}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

export const runsApi = {
  // Unwrap the PagedRuns envelope so callers always see a plain array.
  // The paginated shape is preserved on `.pagedList` for future paging needs.
  list: async (wsId) => {
    const paged = await apiFetch(`/workspaces/${wsId}/runs`);
    return (paged && paged.items) || [];
  },
  pagedList: (wsId, params = {}) => {
    const q = new URLSearchParams(params).toString();
    const suffix = q ? `?${q}` : "";
    return apiFetch(`/workspaces/${wsId}/runs${suffix}`);
  },
  get: (wsId, runId) => apiFetch(`/workspaces/${wsId}/runs/${runId}`),
  create: (wsId, body) =>
    apiFetch(`/workspaces/${wsId}/runs`, { method: "POST", body }),
  processes: (wsId, runId) =>
    apiFetch(`/workspaces/${wsId}/runs/${runId}/processes`),
  processDetail: (wsId, runId, processId) =>
    apiFetch(`/workspaces/${wsId}/runs/${runId}/processes/${processId}`),
  findings: (wsId, runId, params = {}) => {
    const q = new URLSearchParams(params).toString();
    const suffix = q ? `?${q}` : "";
    return apiFetch(`/workspaces/${wsId}/runs/${runId}/findings${suffix}`);
  },
  dataset: (wsId, runId) =>
    apiFetch(`/workspaces/${wsId}/runs/${runId}/dataset`),
  processProvenance: (wsId, runId, processId) =>
    apiFetch(
      `/workspaces/${wsId}/runs/${runId}/processes/${processId}/provenance`,
    ),
  findingProvenance: (wsId, runId, findingId) =>
    apiFetch(
      `/workspaces/${wsId}/runs/${runId}/findings/${findingId}/provenance`,
    ),
};

// ---------------------------------------------------------------------------
// Imports (wizard)
// ---------------------------------------------------------------------------

export const importsApi = {
  list: (wsId) => apiFetch(`/workspaces/${wsId}/imports`),
  get: (wsId, importId) =>
    apiFetch(`/workspaces/${wsId}/imports/${importId}`),
  upload: (wsId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch(`/workspaces/${wsId}/imports`, {
      method: "POST",
      body: fd,
      formData: true,
    });
  },
  sheetMapping: (wsId, importId, mappings) =>
    apiFetch(`/workspaces/${wsId}/imports/${importId}/sheet_mapping`, {
      method: "POST",
      body: { mappings },
    }),
  columnMapping: (wsId, importId, body) =>
    apiFetch(`/workspaces/${wsId}/imports/${importId}/column_mapping`, {
      method: "POST",
      body,
    }),
  preview: (wsId, importId) =>
    apiFetch(`/workspaces/${wsId}/imports/${importId}/preview`, {
      method: "POST",
    }),
  commit: (wsId, importId, body) =>
    apiFetch(`/workspaces/${wsId}/imports/${importId}/commit`, {
      method: "POST",
      body,
    }),
};

// ---------------------------------------------------------------------------
// Mapping profiles
// ---------------------------------------------------------------------------

export const mappingProfilesApi = {
  list: (wsId) => apiFetch(`/workspaces/${wsId}/mapping_profiles`),
  get: (wsId, id) => apiFetch(`/workspaces/${wsId}/mapping_profiles/${id}`),
};

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export const templatesApi = {
  canonicalUrl: (sample = false) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/templates/canonical${sample ? "?sample=1" : ""}`,
};

// ---------------------------------------------------------------------------
// Platform
// ---------------------------------------------------------------------------

export const platformApi = {
  engine: () => apiFetch("/platform/engine"),
};

// ---------------------------------------------------------------------------
// Remediation (Phase 6d)
// ---------------------------------------------------------------------------

export const remediationsApi = {
  list: (wsId, params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const q = qs.toString();
    return apiFetch(`/workspaces/${wsId}/remediations${q ? "?" + q : ""}`);
  },
  upsertForFinding: (wsId, findingId, body) =>
    apiFetch(`/workspaces/${wsId}/findings/${findingId}/remediation`, {
      method: "POST",
      body,
    }),
  patch: (wsId, remediationId, body) =>
    apiFetch(`/workspaces/${wsId}/remediations/${remediationId}`, {
      method: "PATCH",
      body,
    }),
};

// ---------------------------------------------------------------------------
// Run comparison (Phase 6e)
// ---------------------------------------------------------------------------

export const comparisonsApi = {
  compare: (wsId, fromRunId, toRunId) =>
    apiFetch(
      `/workspaces/${wsId}/runs/${fromRunId}/compare/${toRunId}`,
    ),
};

// ---------------------------------------------------------------------------
// AI (Phase 6b)
// ---------------------------------------------------------------------------

export const aiApi = {
  explainFinding: (wsId, runId, findingId) =>
    apiFetch(
      `/workspaces/${wsId}/runs/${runId}/findings/${findingId}/explain`,
      { method: "POST" },
    ),
  analyseStrategies: (wsId, runId, processId) =>
    apiFetch(
      `/workspaces/${wsId}/runs/${runId}/processes/${processId}/strategies`,
      { method: "POST" },
    ),
  listStrategyAnalyses: (wsId, { limit = 50 } = {}) =>
    apiFetch(`/workspaces/${wsId}/strategies?limit=${limit}`),
  validationRecommendation: (wsId, runId, findingId, { polish = false } = {}) =>
    apiFetch(
      `/workspaces/${wsId}/runs/${runId}/findings/${findingId}/validation${polish ? "?polish=true" : ""}`,
      { method: "POST" },
    ),
};
